import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Minus, Plus, Wine, MapPin, Thermometer, Clock, Grape, Truck, ShieldCheck, Lock, Weight, Tag, Droplet, Sparkles, Check, Globe, GlassWater, Utensils, Calendar, ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { productsStore, Product, initializeCache, settingsStore } from '@/stores/localStorageStore';
import CartDrawer from '@/components/CartDrawer';
import { productImages } from '@/lib/images';
import { toast } from 'sonner';
import ProductImage from '@/components/ProductImage';
import { generateSlug } from '@/lib/utils';

const getThumbUrl = (url: string) => {
  if (!url) return url;
  if (url.includes('_thumb.webp')) return url;
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return url;
  return url.substring(0, lastDotIndex) + '_thumb.webp';
};

const getCategoryUrl = (categoryName: string) => {
  return `/produkty/${categoryName.toLowerCase().replace(/\s+/g, '-')}`;
};

const getProductUrl = (prod: Product) => {
  return `/produkty/${prod.category}/${prod.id}/${generateSlug(prod.name)}`;
};

const normalizeProductName = (name: string) => {
  return name
    .toLowerCase()
    .replace(/\s*-?\s*\d+([\.,]\d+)?\s*(g|l|ml|kg|szt\.)/gi, '')
    .replace(/[-–—]/g, ' ')
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const renderFormattedDescription = (text: string) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Support markdown style bold elements: **text** -> <strong>text</strong>
    const parseFormatting = (str: string): React.ReactNode[] => {
      const parts = str.split(/(\*\*.*?\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-bold text-primary">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };
    
    if (trimmed.startsWith('-')) {
      const content = trimmed.substring(1).trim();
      currentList.push(
        <li key={`li-${index}`} className="list-disc list-inside ml-2 mb-1.5 text-foreground/80 last:mb-0">
          {parseFormatting(content)}
        </li>
      );
    } else {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`ul-${index}`} className="my-3 space-y-1 pl-2 list-none">
            {currentList}
          </ul>
        );
        currentList = [];
      }
      
      if (trimmed === '') {
        elements.push(<div key={`br-${index}`} className="h-3"></div>);
      } else {
        elements.push(
          <p key={`p-${index}`} className="mb-2 last:mb-0 leading-relaxed text-foreground/80">
            {parseFormatting(trimmed)}
          </p>
        );
      }
    }
  });
  
  if (currentList.length > 0) {
    elements.push(
      <ul key={`ul-end`} className="my-3 space-y-1 pl-2 list-none">
        {currentList}
      </ul>
    );
  }
  
  return <div className="space-y-1">{elements}</div>;
};

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { addToCart, isCartOpen, setIsCartOpen } = useCart();

  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [variants, setVariants] = useState<Product[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isAdded, setIsAdded] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadProduct = async () => {
      if (!id) return;

      if (id === 'preview') {
        const previewRaw = localStorage.getItem('previewProduct');
        if (previewRaw) {
          setProduct(JSON.parse(previewRaw));
          setRelatedProducts([]);
          setVariants([]);
          setSelectedImage(null);
          setCurrentImageIndex(0);
          return;
        }
      }

      // 1. Try to get from store synchronously
      let foundProduct = productsStore.getById(id);

      // 2. If not found, try to initialize cache (fetch from API) and retry
      if (!foundProduct) {
        try {
          await initializeCache();
          foundProduct = productsStore.getById(id);
        } catch (error) {
          console.error("Failed to load product data:", error);
        }
      }

      // 3. Set state if found
      if (foundProduct) {
        if ((foundProduct.status === 'draft' || foundProduct.status === 'pos_only') && id !== 'preview') {
          setProduct(null);
          return;
        }

        // SEO Redirect: jeśli URL nie ma poprawnego formatu ze slugiem, przekieruj
        const expectedSlug = generateSlug(foundProduct.name);
        const expectedPath = `/produkty/${foundProduct.category}/${foundProduct.id}/${expectedSlug}`;
        
        // Sprawdzamy czy aktualny pathname pasuje do docelowego. Jeśli nie, robimy replace.
        if (window.location.pathname !== expectedPath && id !== 'preview') {
          navigate(expectedPath, { replace: true });
        }

        setProduct(foundProduct);
        const related = productsStore.getFiltered({ category: foundProduct.category })
          .filter(p => p.id !== id && p.status !== 'draft' && p.status !== 'pos_only')
          .slice(0, 4);
        setRelatedProducts(related);

        // Load variants (other sizes/capacities)
        const currentNormalized = normalizeProductName(foundProduct.name);
        const allProducts = productsStore.getAll();
        const otherVariants = allProducts.filter(p => {
          if (p.id === foundProduct.id) return false;
          if (p.status === 'draft' || p.status === 'pos_only') return false;
          return normalizeProductName(p.name) === currentNormalized;
        });
        setVariants(otherVariants);

        // Reset gallery state
        setSelectedImage(null);
        setCurrentImageIndex(0);
      }
    };

    loadProduct();
  }, [id, navigate]);

  useEffect(() => {
    if (isAdded) {
      const timer = setTimeout(() => setIsAdded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isAdded]);

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <SEO title="Nie znaleziono produktu - Włoska Piwnica" description="Produkt nie istnieje lub został usunięty." noindex />
        <p className="mb-4 text-xl">{language === 'PL' ? 'Produkt nie znaleziony' : 'Product not found'}</p>
        <Link to="/produkty" className="text-primary hover:underline">
          {language === 'PL' ? 'Wróć do produktów' : 'Back to products'}
        </Link>
      </div>
    );
  }

  // --- Image Logic Moved Up ---

  // helper to get image url (key or path)
  const getImageUrl = (img?: string) => {
    if (!img) return '';
    // Sprawdź mapę kluczy obrazów
    if (productImages[img as keyof typeof productImages]) {
      return productImages[img as keyof typeof productImages];
    }
    // Jeśli ścieżka zaczyna się od /api/ to jest ścieżka bezpośrednia
    if (img.startsWith('/api/') || img.startsWith('http')) {
      return img;
    }
    // Fallback — spróbuj jako ścieżka do uploads
    return `/api/images/uploads/${img}`;
  };

  // Prepare images list (main image + additional images)
  const allImages = product ? [
    ...(product.images && product.images.length > 0 ? product.images : [product.image || 'primitivo'])
  ] : [];

  const mainImage = selectedImage || allImages[0];

  const handleAddToCart = () => {
    const imageToUse = getImageUrl(mainImage || product?.image);

    for (let i = 0; i < quantity; i++) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.salePrice ?? product.price,
        originalPrice: product.salePrice ? product.price : undefined,
        image: imageToUse,
        maxStock: product.stock,
        inStoreOnly: product.inStoreOnly
      });
    }
    setIsAdded(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    setSelectedImage(allImages[(currentImageIndex + 1) % allImages.length]);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    setSelectedImage(allImages[(currentImageIndex - 1 + allImages.length) % allImages.length]);
  };

  const handleThumbnailClick = (img: string, idx: number) => {
    setSelectedImage(img);
    setCurrentImageIndex(idx);
  };

  const currentPrice = product.salePrice ?? product.price;
  const description = language === 'EN' && product.descriptionEn ? product.descriptionEn : product.description || '';

  const productUrl = `/produkty/${product.category}/${product.id}/${generateSlug(product.name)}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        "name": product.name,
        "image": getImageUrl(mainImage),
        "description": description,
        "sku": product.id,
        "brand": {
          "@type": "Brand",
          "name": "Włoska Piwnica"
        },
        "offers": {
          "@type": "Offer",
          "url": `https://wloska-piwnica.pl${productUrl}`,
          "priceCurrency": "PLN",
          "price": currentPrice.toFixed(2),
          "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          "itemCondition": "https://schema.org/NewCondition",
          "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://wloska-piwnica.pl/" },
          { "@type": "ListItem", "position": 2, "name": language === 'EN' ? 'Products' : 'Produkty', "item": "https://wloska-piwnica.pl/produkty" },
          { "@type": "ListItem", "position": 3, "name": product.name }
        ]
      }
    ]
  };

  return (
    <article className="min-h-screen bg-background text-foreground">
      <SEO 
        title={`${product.name} - Włoska Piwnica`}
        description={description}
        canonicalUrl={productUrl}
        ogImage={getImageUrl(mainImage)}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="container mx-auto px-4 py-12 pt-32 max-w-7xl">
        {/* Breadcrumbs */}
        <nav className="mb-12 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium">
          <Link to="/" className="hover:text-primary transition-colors">{language === 'PL' ? 'Kolekcja' : 'Collection'}</Link>
          <span className="text-border">/</span>
          <Link to="/produkty" className="hover:text-primary transition-colors">{t(product.category)}</Link>
          <span className="text-border">/</span>
          <span className="text-primary font-bold">{product.name}</span>
        </nav>

        <div className="grid gap-8 lg:gap-16 lg:grid-cols-12 items-start">

          {/* Left Column: Gallery (6 Cols) */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            <div className="relative group/gallery">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative aspect-[4/5] w-full overflow-hidden bg-[#F9F7F2] rounded-2xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] ring-1 ring-black/5 cursor-zoom-in"
                onClick={() => setIsLightboxOpen(true)}
              >
                <motion.div
                  key={mainImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="h-full w-full"
                >
                  <ProductImage
                    src={getThumbUrl(getImageUrl(mainImage))}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform duration-1000"
                  />
                </motion.div>

                {product.stock < 10 && product.stock > 0 && (
                  <div className="absolute left-6 top-6 bg-primary/95 backdrop-blur-md px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-white rounded-full shadow-lg z-10">
                    {language === 'PL' ? `Wyjątkowa okazja: ${product.stock} szt.` : `Limited: ${product.stock} pcs`}
                  </div>
                )}

                {/* Navigation Arrows */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-md border border-black/5 shadow-md text-primary opacity-0 group-hover/gallery:opacity-100 transition-opacity duration-300 hover:bg-white z-10"
                    >
                      <ChevronLeft className="h-6 w-6" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-md border border-black/5 shadow-md text-primary opacity-0 group-hover/gallery:opacity-100 transition-opacity duration-300 hover:bg-white z-10"
                    >
                      <ChevronRight className="h-6 w-6" />
                    </button>
                  </>
                )}

                <div className="absolute right-6 bottom-6 flex flex-col gap-2 z-10">
                  <div className="h-10 w-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm border border-black/5">
                    <Maximize2 className="h-4 w-4 text-primary" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-1">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleThumbnailClick(img, idx)}
                    className={`relative aspect-square w-20 flex-shrink-0 overflow-hidden rounded-xl border-2 transition-all duration-300 ${mainImage === img ? 'border-primary shadow-md scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                  >
                    <div className="h-full w-full bg-[#F9F7F2] p-2">
                      <ProductImage src={getThumbUrl(getImageUrl(img))} alt={`${product.name} ${idx + 1}`} className="h-full w-full object-contain" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Info (6 Cols) */}
          <div className="lg:col-span-6 flex flex-col pt-4">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Product Header */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-[1px] w-8 bg-primary/30"></div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary">
                    {product.region || (language === 'PL' ? 'Włoska piwnica' : 'Premium Vineyard')}
                  </span>
                </div>
                <h1 className="font-display text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-normal leading-[1.1] text-foreground tracking-tight break-words">
                  {product.name}
                </h1>
                <p className="text-muted-foreground text-lg leading-relaxed font-serif italic">
                  {language === 'EN' && product.descriptionEn ? product.descriptionEn : product.description}
                </p>
              </div>

              {/* Price Block */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-6 border-y border-border/40 gap-4">
                <div className="space-y-1">
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl sm:text-4xl font-display font-medium text-primary">
                      {product.salePrice ? product.salePrice.toFixed(2) : product.price.toFixed(2)} zł
                    </span>
                    {product.salePrice && (
                      <span className="text-xl text-muted-foreground line-through decoration-primary/30">
                        {product.price.toFixed(2)} zł
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? `VAT wliczony • Darmowa dostawa od ${settingsStore.get().freeShippingThreshold} zł` : `VAT included • Free delivery from ${settingsStore.get().freeShippingThreshold} zł`}</p>
                </div>

                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${product.stock > 0
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                  : 'bg-red-50 text-red-700 border-red-100'}`}>
                  <div className={`h-2 w-2 rounded-full animate-pulse ${product.stock > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                  <span className="text-[10px] font-bold uppercase">
                    {product.stock > 0
                      ? (language === 'PL' ? `W magazynie: ${product.stock} szt.` : `In stock: ${product.stock} pcs`)
                      : (language === 'PL' ? 'Brak w magazynie' : 'Out of stock')}
                  </span>
                </div>
              </div>

              {/* Add to Cart Section */}
              <div className="space-y-6">
                {product.inStoreOnly && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                    <div className="flex items-start gap-3 text-amber-800">
                      <div className="mt-0.5 shrink-0 rounded-full bg-amber-100 p-1">
                        <ShoppingCart className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="text-sm font-medium">
                        {language === 'PL' 
                          ? 'Ten produkt wymaga odbioru osobistego. Jeśli dodasz go do koszyka, jedyną dostępną metodą dostawy dla całego zamówienia będzie odbiór w sklepie stacjonarnym.' 
                          : 'This product requires local pickup. If added to cart, the only available delivery method for the entire order will be in-store pickup.'}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Warianty pojemności / wagi */}
                {variants.length > 0 && (
                  <div className="space-y-3 pb-2">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 block">
                      {language === 'PL' ? 'Dostępne pojemności / opakowania:' : 'Available sizes / packaging:'}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {/* Bieżący wariant */}
                      <div className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-white border border-primary cursor-default shadow-sm">
                        {product.volume || product.weight || (language === 'PL' ? 'Bieżący' : 'Current')}
                      </div>
                      
                      {/* Inne warianty */}
                      {variants.map((v) => {
                        const isOutOfStock = v.stock <= 0;
                        const displaySize = v.volume || v.weight || (language === 'PL' ? 'Inny' : 'Other');
                        const priceDisplay = v.salePrice ? v.salePrice.toFixed(2) : v.price.toFixed(2);
                        
                        return (
                          <Link
                            key={v.id}
                            to={getProductUrl(v)}
                            className={`px-4 py-2 text-xs font-semibold rounded-xl border transition-all shadow-sm ${
                              isOutOfStock
                                ? 'bg-[#f4f2ec] border-dashed border-border text-muted-foreground/60 line-through hover:border-primary/40 hover:text-primary'
                                : 'bg-white border-border text-foreground hover:border-primary hover:text-primary hover:shadow-md'
                            }`}
                            title={isOutOfStock ? (language === 'PL' ? 'Brak w magazynie - kliknij by przejść' : 'Out of stock - click to view') : undefined}
                          >
                            {displaySize} - {priceDisplay} zł {isOutOfStock && (language === 'PL' ? '(brak)' : '(out)')}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex items-center rounded-2xl bg-[#F9F7F2] p-1 border border-black/5 shadow-inner">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="h-12 w-12 flex items-center justify-center rounded-xl hover:bg-white transition-all text-muted-foreground hover:text-primary disabled:opacity-30"
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-10 text-center font-display text-lg font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="h-12 w-12 flex items-center justify-center rounded-xl hover:bg-white transition-all text-muted-foreground hover:text-primary"
                      disabled={quantity >= product.stock}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    disabled={product.stock === 0}
                    className={`flex-1 relative overflow-hidden group rounded-2xl px-8 h-14 flex items-center justify-center gap-3 transition-all duration-500 ${
                      product.inStoreOnly && !isAdded
                        ? 'bg-amber-600 shadow-[0_10px_30px_-10px_rgba(217,119,6,0.4)] hover:shadow-[0_15px_40px_-10px_rgba(217,119,6,0.6)] hover:-translate-y-1'
                        : isAdded
                          ? 'bg-emerald-600 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)]'
                          : 'bg-primary shadow-[0_10px_30px_-10px_rgba(114,24,39,0.4)] hover:shadow-[0_15px_40px_-10px_rgba(114,24,39,0.6)] hover:-translate-y-1'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    {isAdded ? (
                      <>
                        <Check className="h-5 w-5 text-white animate-in zoom-in" />
                        <span className="text-white font-bold uppercase tracking-widest text-xs">{language === 'PL' ? 'Dodano' : 'Added'}</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                        <span className="text-white font-bold uppercase tracking-widest text-xs">
                          {product.stock === 0 ? (language === 'PL' ? 'Brak' : 'Out of stock') : 
                            product.inStoreOnly ? (language === 'PL' ? 'Do koszyka (tylko odbiór)' : 'Add to cart (pickup)') : 
                            (language === 'PL' ? 'Do koszyka' : 'Add to cart')}
                        </span>
                      </>
                    )}
                  </button>

                </div>

                {/* Secure checkout notice */}
                <div className="flex items-center justify-center gap-6 py-2">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    <Lock className="h-3 w-3" />
                    <span>{language === 'PL' ? 'Bezpieczna płatność' : 'Secure Checkout'}</span>
                  </div>
                  <div className="h-1 w-1 rounded-full bg-border"></div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    <Truck className="h-3 w-3" />
                    <span>{language === 'PL' ? 'Dostawa 24-48h' : 'Delivery 24-48h'}</span>
                  </div>
                </div>
              </div>

              {/* Technical Grid: The Sommelier's View */}
              {(product.year || product.grapeVariety || product.region || product.weight || product.alcohol || product.volume || product.servingTemp || product.sweetness || product.fizziness) && (
                <div className="pt-8 space-y-6">
                  <div className="flex items-center gap-3">
                    <h3 className="text-[12px] font-bold uppercase tracking-[0.2em]">{language === 'PL' ? 'Szczegóły produktu' : "Product Details"}</h3>
                    <div className="flex-1 h-[1px] bg-border/40"></div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                    {product.year && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Calendar className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Rocznik' : 'Vintage'}</p>
                          <p className="font-medium text-sm">{product.year}</p>
                        </div>
                      </div>
                    )}

                    {product.grapeVariety && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Grape className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Szczep' : 'Grapes'}</p>
                          <p className="font-medium text-sm truncate max-w-[140px]" title={product.grapeVariety}>{product.grapeVariety}</p>
                        </div>
                      </div>
                    )}

                    {product.region && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Globe className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Region' : 'Region'}</p>
                          <p className="font-medium text-sm">{product.region}</p>
                        </div>
                      </div>
                    )}

                    {product.alcohol && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Droplet className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Alkohol' : 'Alcohol'}</p>
                          <p className="font-medium text-sm">{product.alcohol}</p>
                        </div>
                      </div>
                    )}

                    {product.volume && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <GlassWater className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Pojemność' : 'Volume'}</p>
                          <p className="font-medium text-sm">{product.volume}</p>
                        </div>
                      </div>
                    )}

                    {product.weight && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Weight className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Waga / Gramatura' : 'Weight'}</p>
                          <p className="font-medium text-sm">{product.weight}</p>
                        </div>
                      </div>
                    )}

                    {product.servingTemp && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Thermometer className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Temp. podania' : 'Serving Temp'}</p>
                          <p className="font-medium text-sm">{product.servingTemp}</p>
                        </div>
                      </div>
                    )}

                    {product.sweetness && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Wine className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Słodkość' : 'Sweetness'}</p>
                          <p className="font-medium text-sm">
                            {product.sweetness === 'dry' ? (language === 'PL' ? 'Wytrawne' : 'Dry') :
                             product.sweetness === 'semi-dry' ? (language === 'PL' ? 'Półwytrawne' : 'Semi-dry') :
                             product.sweetness === 'semi-sweet' ? (language === 'PL' ? 'Półsłodkie' : 'Semi-sweet') :
                             product.sweetness === 'sweet' ? (language === 'PL' ? 'Słodkie' : 'Sweet') : product.sweetness}
                          </p>
                        </div>
                      </div>
                    )}

                    {product.fizziness && (
                      <div className="flex items-start gap-4">
                        <div className="mt-1 h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center border border-primary/10">
                          <Sparkles className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{language === 'PL' ? 'Rodzaj' : 'Type'}</p>
                          <p className="font-medium text-sm">
                            {product.fizziness === 'still' ? (language === 'PL' ? 'Spokojne' : 'Still') :
                             product.fizziness === 'sparkling' ? (language === 'PL' ? 'Musujące' : 'Sparkling') : product.fizziness}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </motion.div>
          </div>
        </div>

        {/* --- FULL WIDTH DESCRIPTION AND PAIRING SECTION --- */}
        {((product.fullDescription || product.description) || (product.pairing && product.pairing.length > 0)) && (
          <section className="mt-16 sm:mt-24 pt-16 border-t border-border/40">
            <div className="max-w-4xl mx-auto">
              {/* Description */}
              {(product.fullDescription || product.description) && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6 }}
                  className="mb-20 text-center space-y-8"
                >
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="h-[1px] w-12 bg-primary/20"></div>
                    <Wine className="h-5 w-5 text-primary/40" />
                    <div className="h-[1px] w-12 bg-primary/20"></div>
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-foreground font-normal">
                    {language === 'PL' ? 'Opis' : 'Tasting Notes'}
                  </h2>
                  <div className="relative rounded-3xl bg-[#F9F7F2]/60 p-8 sm:p-12 md:p-16 border border-black/5 shadow-sm mt-8">
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-primary/20 font-display text-8xl select-none leading-none">"</div>
                    <div className="relative z-10 text-base sm:text-lg leading-relaxed text-foreground/85 font-serif max-w-3xl mx-auto text-left">
                      {renderFormattedDescription(language === 'EN' && (product.fullDescriptionEn || product.descriptionEn) ? (product.fullDescriptionEn || product.descriptionEn) : (product.fullDescription || product.description))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Pairings */}
              {product.pairing && product.pairing.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="space-y-10"
                >
                  <div className="text-center space-y-4">
                    <h3 className="text-[13px] sm:text-sm font-bold uppercase tracking-[0.3em] text-primary">
                      {language === 'PL' ? 'Sugerowane połączenia' : 'Culinary Pairings'}
                    </h3>
                  </div>
                  
                  <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
                    {product.pairing.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 px-6 py-3 bg-white rounded-2xl border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-primary/20 transition-all duration-400 cursor-default group">
                        <div className="h-10 w-10 rounded-full bg-[#F9F7F2] flex items-center justify-center group-hover:bg-primary/5 transition-colors duration-400">
                          <Utensils className="h-4 w-4 text-primary/60 group-hover:text-primary transition-colors duration-400" />
                        </div>
                        <span className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors duration-400">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </section>
        )}

        {relatedProducts.length > 0 && (
          <section className="mt-16 sm:mt-32 border-t border-border pt-12 sm:pt-20">
            <div className="flex flex-col items-center mb-16 space-y-4 text-center">
              <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary">{language === 'PL' ? 'Eksploruj dalej' : 'Explore more'}</span>
              <h2 className="font-display text-2xl sm:text-4xl text-foreground">
                {language === 'PL' ? 'Polecane dla Ciebie' : 'Recommended for You'}
              </h2>
            </div>
            <div className="grid gap-x-12 gap-y-16 sm:grid-cols-2 lg:grid-cols-4">
              {relatedProducts.map((relatedProduct) => (
                <Link key={relatedProduct.id} to={`/product/${relatedProduct.id}`}
                  className="group block">
                  <div className="relative mb-6 aspect-[3/4] overflow-hidden bg-[#F9F7F2] rounded-2xl ring-1 ring-black/5 transition-all duration-500 group-hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.2)]">
                    <ProductImage
                      src={getThumbUrl(getImageUrl(relatedProduct.image || (relatedProduct.images && relatedProduct.images[0])))}
                      alt={relatedProduct.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500"></div>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground/60 transition-colors group-hover:text-primary">{t(relatedProduct.category)}</h3>
                    <h4 className="font-display text-xl text-foreground group-hover:text-primary transition-colors">{relatedProduct.name}</h4>
                    <p className="font-display text-lg text-primary pt-1">{relatedProduct.price.toFixed(2)} zł</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Lightbox / Gallery Modal */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl"
            onClick={() => setIsLightboxOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative max-w-5xl w-full h-[85vh] flex items-center justify-center px-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsLightboxOpen(false)}
                className="absolute top-0 right-4 p-2 text-white/70 hover:text-white transition-colors"
                aria-label="Close lightbox"
              >
                <X className="h-8 w-8" />
              </button>

              {allImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-14 w-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white transition-all"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 h-14 w-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white transition-all"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                </>
              )}

              <div className="w-full h-full flex items-center justify-center p-4">
                <motion.img
                  key={mainImage}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  src={getImageUrl(mainImage)}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain pointer-events-none drop-shadow-2xl"
                />
              </div>

              {/* Lightbox Thumbnails */}
              {allImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 px-4 py-3 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 overflow-x-auto max-w-[90vw] scrollbar-hide">
                  {allImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleThumbnailClick(img, idx)}
                      className={`relative aspect-square w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-300 ${mainImage === img ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-40 hover:opacity-100'
                        }`}
                    >
                      <img src={getThumbUrl(getImageUrl(img))} alt={`Lightbox ${idx + 1}`} loading="lazy" className="h-full w-full object-contain p-1" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
};

export default ProductDetailPage;

